import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckSquare, Square, AlertCircle } from 'lucide-react';

interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
}

interface PreStartChecklistProps {
  items: ChecklistItem[];
  onComplete: () => void;
}

export const PreStartChecklist: React.FC<PreStartChecklistProps> = ({ items, onComplete }) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const requiredAll = items.filter(i => i.required).every(i => checked.has(i.id));

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <h2 className="text-lg font-bold text-aero-900 mb-1">{t('candidate.ojt.preStart.title')}</h2>
        <p className="text-sm text-aero-500">{t('candidate.ojt.preStart.desc')}</p>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => toggle(item.id)}
            className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${
              checked.has(item.id)
                ? 'border-green-400 bg-green-50'
                : 'border-aero-200 bg-white hover:border-aero-300'
            }`}
          >
            {checked.has(item.id) ? (
              <CheckSquare className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            ) : (
              <Square className="w-5 h-5 text-aero-300 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-aero-900">{item.label}</p>
              {item.required && (
                <span className="text-[10px] font-bold text-red-500 uppercase tracking-wide">Required</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {!requiredAll && (
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
          <p className="text-xs text-yellow-700">Please confirm all required items before continuing.</p>
        </div>
      )}

      <button
        onClick={onComplete}
        disabled={!requiredAll}
        className="w-full py-3.5 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 disabled:text-aero-400 text-white rounded-xl font-semibold text-sm transition-colors"
      >
        {t('candidate.ojt.preStart.confirm')}
      </button>
    </div>
  );
};
